import { createApp } from 'vue'
import App from './App.vue'
import router from './router/index.js'
import ViewUIPlus from 'view-ui-plus'
import 'view-ui-plus/dist/styles/viewuiplus.css'

import {
  ElAvatar,
  ElBadge,
  ElButton,
  ElButtonGroup,
  ElCard,
  ElCarousel,
  ElCarouselItem,
  ElCheckbox,
  ElCheckboxGroup,
  ElCol,
  ElColorPicker,
  ElDatePicker,
  ElDialog,
  ElDivider,
  ElDrawer,
  ElDropdown,
  ElDropdownItem,
  ElDropdownMenu,
  ElFooter,
  ElForm,
  ElFormItem,
  ElIcon,
  ElImage,
  ElInput,
  ElInputNumber,
  ElLink,
  ElMenu,
  ElMenuItem,
  ElOption,
  ElPageHeader,
  ElPagination,
  ElProgress,
  ElRadio,
  ElRadioButton,
  ElRadioGroup,
  ElResult,
  ElRow,
  ElSelect,
  ElSkeleton,
  ElSubMenu,
  ElSwitch,
  ElTabPane,
  ElTable,
  ElTableColumn,
  ElTabs,
  ElTag,
  ElUpload,
  ElLoadingDirective,
} from 'element-plus'

import 'element-plus/es/components/avatar/style/css'
import 'element-plus/es/components/badge/style/css'
import 'element-plus/es/components/button/style/css'
import 'element-plus/es/components/button-group/style/css'
import 'element-plus/es/components/card/style/css'
import 'element-plus/es/components/carousel/style/css'
import 'element-plus/es/components/carousel-item/style/css'
import 'element-plus/es/components/checkbox/style/css'
import 'element-plus/es/components/checkbox-group/style/css'
import 'element-plus/es/components/col/style/css'
import 'element-plus/es/components/color-picker/style/css'
import 'element-plus/es/components/date-picker/style/css'
import 'element-plus/es/components/dialog/style/css'
import 'element-plus/es/components/divider/style/css'
import 'element-plus/es/components/drawer/style/css'
import 'element-plus/es/components/dropdown/style/css'
import 'element-plus/es/components/dropdown-item/style/css'
import 'element-plus/es/components/dropdown-menu/style/css'
import 'element-plus/es/components/footer/style/css'
import 'element-plus/es/components/form/style/css'
import 'element-plus/es/components/form-item/style/css'
import 'element-plus/es/components/icon/style/css'
import 'element-plus/es/components/image/style/css'
import 'element-plus/es/components/input/style/css'
import 'element-plus/es/components/input-number/style/css'
import 'element-plus/es/components/link/style/css'
import 'element-plus/es/components/menu/style/css'
import 'element-plus/es/components/menu-item/style/css'
import 'element-plus/es/components/option/style/css'
import 'element-plus/es/components/page-header/style/css'
import 'element-plus/es/components/pagination/style/css'
import 'element-plus/es/components/progress/style/css'
import 'element-plus/es/components/radio/style/css'
import 'element-plus/es/components/radio-button/style/css'
import 'element-plus/es/components/radio-group/style/css'
import 'element-plus/es/components/result/style/css'
import 'element-plus/es/components/row/style/css'
import 'element-plus/es/components/select/style/css'
import 'element-plus/es/components/skeleton/style/css'
import 'element-plus/es/components/sub-menu/style/css'
import 'element-plus/es/components/switch/style/css'
import 'element-plus/es/components/tab-pane/style/css'
import 'element-plus/es/components/table/style/css'
import 'element-plus/es/components/table-column/style/css'
import 'element-plus/es/components/tabs/style/css'
import 'element-plus/es/components/tag/style/css'
import 'element-plus/es/components/upload/style/css'
import 'element-plus/es/components/loading/style/css'
import 'element-plus/es/components/message/style/css'
import 'element-plus/es/components/message-box/style/css'
import 'element-plus/es/components/notification/style/css'

import './styles/admin-theme.css'
import store from './store'
import axios from 'axios'
import VueAxios from 'vue-axios'
import qs from 'qs'
import UUID from 'vue-uuid'
import VueCalendarHeatmap from 'vue3-calendar-heatmap'
import { installLoginRequiredPromptInterceptor } from './utils/loginRequiredPrompt'
import logger from './utils/logger'

const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || ''
axios.defaults.baseURL = apiBaseUrl
axios.defaults.xsrfHeaderName = 'X-CSRFToken'
axios.defaults.xsrfCookieName = 'csrftoken'

axios.interceptors.request.use(
  function (config) {
    config.headers['Authorization'] = 'Bearer ' + store.state.token
    return config
  },
  function (error) {
    return Promise.reject(error)
  }
)

const app = createApp(App)

const elementComponents = [
  ElAvatar,
  ElBadge,
  ElButton,
  ElButtonGroup,
  ElCard,
  ElCarousel,
  ElCarouselItem,
  ElCheckbox,
  ElCheckboxGroup,
  ElCol,
  ElColorPicker,
  ElDatePicker,
  ElDialog,
  ElDivider,
  ElDrawer,
  ElDropdown,
  ElDropdownItem,
  ElDropdownMenu,
  ElFooter,
  ElForm,
  ElFormItem,
  ElIcon,
  ElImage,
  ElInput,
  ElInputNumber,
  ElLink,
  ElMenu,
  ElMenuItem,
  ElOption,
  ElPageHeader,
  ElPagination,
  ElProgress,
  ElRadio,
  ElRadioButton,
  ElRadioGroup,
  ElResult,
  ElRow,
  ElSelect,
  ElSkeleton,
  ElSubMenu,
  ElSwitch,
  ElTabPane,
  ElTable,
  ElTableColumn,
  ElTabs,
  ElTag,
  ElUpload,
]

for (const component of elementComponents) {
  app.component(component.name, component)
}
app.directive('loading', ElLoadingDirective)

app
  .use(store)
  .use(router)
  .use(VueAxios, axios)
  .use(qs)
  .use(ViewUIPlus)
  .use(UUID)
  .use(VueCalendarHeatmap)

installLoginRequiredPromptInterceptor(app, router)
app.provide('axios', app.config.globalProperties.axios)

if (typeof window !== 'undefined') {
  window.__CODEAC_LOGGER__ = logger
}

app.config.errorHandler = (err, instance, info) => {
  logger.error('VueError', {
    info,
    route: router.currentRoute.value?.fullPath || '',
    message: err?.message || String(err),
    stack: err?.stack || '',
  })
}

if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    logger.error('WindowError', {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      error: event.error?.stack || event.error?.message || '',
    })
  })

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason
    logger.error('UnhandledRejection', {
      message: reason?.message || String(reason),
      stack: reason?.stack || '',
    })
  })
}

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    logger.error('AxiosError', {
      url: error?.config?.url || '',
      method: error?.config?.method || '',
      status: error?.response?.status || 0,
      message: error?.message || '',
    })
    return Promise.reject(error)
  }
)

app.mount('#app')
